import React, { useEffect, useMemo, useState } from 'react';

import Box from '@amzn/meridian/box';
import Button from '@amzn/meridian/button';
import Modal, { ModalFooter } from '@amzn/meridian/modal';
import Row from '@amzn/meridian/row';
import Select, { SelectOption } from '@amzn/meridian/select';
import LoadingSpinnerWrapper from 'src/components/shared/loadingSpinner/loadingSpinnerWrapper';
import RegionsService from 'src/services/regionsService';
import TasksService from 'src/services/tasks/taskService';
import { type RegionForm } from 'src/store/adminConfigPageSlice/regionTasksPageSlice/types';
import { useAppSelector } from 'src/store/store';
import { createFileUrl, downloadFile } from 'src/utils/fileUtils';
import { createSearchRegExp } from 'src/utils/searchUtils';

const ExportJobsSummaryModal = ({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) => {
  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      title={'Export Jobs And Requirements Summary'}
      closeLabel="Close"
      width="416px"
    >
      <ExportJobsSummaryModalContent {...{ onClose }} />
    </Modal>
  );
};

const ExportJobsSummaryModalContent = ({ onClose }: { onClose: () => void }) => {
  const { token } = useAppSelector((state) => state.user);

  const [isLoading, setIsLoading] = useState(true);
  const [regions, setRegions] = useState<RegionForm[]>([]);
  const [regionSearchQuery, setRegionSearchQuery] = useState('');
  const [selectedRegion, setSelectedRegion] = useState();

  useEffect(() => {
    if (token) {
      TasksService.fetchRegions().then((fetchedRegions) => {
        setRegions(fetchedRegions);
        setIsLoading(false);
      });
    }
  }, [token]);

  const matchedRegionOptions = useMemo(() => {
    const regionSearchRegExp = createSearchRegExp(regionSearchQuery);
    return regions.filter((region) => !regionSearchQuery || regionSearchRegExp.test(region.name));
  }, [regions, regionSearchQuery]);

  const onConfirm = async () => {
    if (selectedRegion === undefined) {
      return;
    }
    const payload = await RegionsService.downloadListJobsWithTaskRequirementsByRegion(selectedRegion);
    // download file
    const regionObj = regions.find((r) => r.id === selectedRegion);
    downloadFile(createFileUrl(payload.data, 'text/csv'), `jobs_with_task_requirements_${regionObj!.name}.csv`);
  };

  return (
    <>
      <LoadingSpinnerWrapper showSpinner={isLoading}>
        <Select
          label="Which region should be exported?"
          searchQuery={regionSearchQuery}
          onSearch={setRegionSearchQuery}
          placeholder="Select region..."
          value={selectedRegion}
          onChange={setSelectedRegion}
        >
          {matchedRegionOptions.map((r) => (
            <SelectOption label={r.name} value={r.id} key={r.id} />
          ))}
        </Select>
        <Box />
        <ModalFooter>
          <Row spacingInset="400 0 0 0" alignmentHorizontal="end" widths="fit">
            <Button type="secondary" size="small" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="primary"
              size="small"
              onClick={onConfirm}
              disabled={isLoading || selectedRegion === undefined}
            >
              Confirm
            </Button>
          </Row>
        </ModalFooter>
      </LoadingSpinnerWrapper>
    </>
  );
};

export default ExportJobsSummaryModal;
